'use client';

import { Button } from 'components/ui/button';
import { Calendar } from 'components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { format } from 'date-fns';
import { ColorType, createChart, createImageWatermark, IChartApi, LineSeries, LineSeriesPartialOptions, Time } from 'lightweight-charts';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import keys from 'lodash/keys';
import map from 'lodash/map';
import padStart from 'lodash/padStart';
import sortBy from 'lodash/sortBy';
import { Calendar as CalendarIcon, ChevronDown, Clock } from 'lucide-react';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ChipLegend, ChipLegendItem } from './chip-legend';

// TypeScript interfaces
interface ForwardCurveProps {
  tradeData?: { data: any[] } | null;
  loading?: boolean;
  onApplyDates?: (frequency: 'D' | 'W' | 'M', periods: number) => void;
  onAddTradeDate?: (date: string) => void;
  onResetDates?: () => void;
  liveData?: { data: any[]; date: string } | null;
}

interface AnalysisMenuProps {
  onApplyDates: (frequency: 'D' | 'W' | 'M', periods: number) => void;
  onClose: () => void;
}

// HSL → hex. Standard CSS-Color-4 a/f conversion trick: f(0), f(8), f(4)
// give R, G, B respectively.
function hsl (h: number, s: number, l: number): string {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number): string => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return padStart(Math.round(255 * c).toString(16), 2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Historic curve snapshots sit on a single solid blue ramp that gets lighter
// the further back the snapshot is — no opacity, so a curve is never faded
// toward invisible against the white chart background (`progress` runs 0 at
// the oldest snapshot to 1 at the most recent, excluding the live/current
// curve, which stays black at 2px below). Lightness is clamped to 68% so
// even the oldest snapshot stays a visible blue, not a near-white smudge.
function historicCurveColor (progress: number): string {
  return hsl(212, 0.65, 0.66 - progress * 0.42); // dark navy (recent) → clamped light blue (old)
}

// AnalysisMenu component
function AnalysisMenu ({ onApplyDates, onClose }: AnalysisMenuProps) {
  // Custom Frequency State
  const [customFreq, setCustomFreq] = useState<'Daily' | 'Weekly' | 'Monthly'>('Weekly');
  const [customCount, setCustomCount] = useState<string>('6');

  // Helper: Map preset to frequency and periods
  const handlePresetClick = (preset: 'weekly-4' | 'weekly-8' | 'monthly-6' | 'monthly-12') => {
    let frequency: 'D' | 'W' | 'M' = 'W';
    let periods = 0;

    // Configuration Map
    if (preset === 'weekly-4') { frequency = 'W'; periods = 4; }
    if (preset === 'weekly-8') { frequency = 'W'; periods = 8; }
    if (preset === 'monthly-6') { frequency = 'M'; periods = 6; }
    if (preset === 'monthly-12') { frequency = 'M'; periods = 12; }

    onApplyDates(frequency, periods);
    onClose();
  };

  // Validate customCount: must be a number between 2 and 50
  const isValidCustomCount = useMemo(() => {
    const num = parseInt(customCount, 10);
    return !isNaN(num) && num >= 2 && num <= 50;
  }, [customCount]);

  const handleCustomApply = () => {
    if (!isValidCustomCount) {
      return; // Don't apply if invalid
    }

    let frequency: 'D' | 'W' | 'M' = 'W';
    if (customFreq === 'Monthly') {
      frequency = 'M';
    } else if (customFreq === 'Weekly') {
      frequency = 'W';
    } else if (customFreq === 'Daily') {
      frequency = 'D';
    }

    const num = parseInt(customCount, 10);
    onApplyDates(frequency, num);
    onClose();
  };

  return (
    <div className="w-64 p-1">
      <div className="space-y-1">
        {/* Section 1: Presets */}
        <div className="grid grid-cols-2 gap-1">
          <button
            onClick={() => handlePresetClick('weekly-4')}
            className="text-left px-2 py-1.5 rounded-sm text-xs hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            Weekly <span className="text-xs text-muted-foreground block">Last 4 Weeks</span>
          </button>
          <button
            onClick={() => handlePresetClick('weekly-8')}
            className="text-left px-2 py-1.5 rounded-sm text-xs hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            Weekly <span className="text-xs text-muted-foreground block">Last 8 Weeks</span>
          </button>
          <button
            onClick={() => handlePresetClick('monthly-6')}
            className="text-left px-2 py-1.5 rounded-sm text-xs hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            Monthly <span className="text-xs text-muted-foreground block">Last 6 Months</span>
          </button>
          <button
            onClick={() => handlePresetClick('monthly-12')}
            className="text-left px-2 py-1.5 rounded-sm text-xs hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            Monthly <span className="text-xs text-muted-foreground block">Last 12 Months</span>
          </button>
        </div>

        <div className="h-px bg-muted -mx-1 my-1"></div>

        {/* Section 2: Arbitrary Frequency */}
        <div>
          <div className="px-2 py-1.5 mb-1">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Custom
            </div>
          </div>

          <div className="space-y-1.5 px-1">
            <div className="flex items-center justify-between text-xs px-1">
              <span className="text-foreground">Frequency:</span>
              <select
                value={customFreq}
                onChange={(e) => setCustomFreq(e.target.value as 'Daily' | 'Weekly' | 'Monthly')}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                className="bg-background border rounded-sm px-1.5 py-0.5 text-xs focus:ring-2 focus:ring-ring outline-hidden"
              >
                <option>Daily</option>
                <option>Weekly</option>
                <option>Monthly</option>
              </select>
            </div>

            <div className="flex items-center justify-between text-xs px-1">
              <span className="text-foreground">Periods Back:</span>
              <input
                type="number"
                min="2"
                max="50"
                value={customCount}
                onChange={(e) => setCustomCount(e.target.value)}
                className="w-14 bg-background border rounded-sm px-1.5 py-0.5 text-xs text-center focus:ring-2 focus:ring-ring outline-hidden"
              />
            </div>

            <button
              onClick={handleCustomApply}
              disabled={!isValidCustomCount}
              className="w-full mt-1 bg-foreground hover:bg-foreground/90 text-background text-xs font-medium py-1.5 px-2 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-foreground"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// CurveHistoryButton component - wraps the button and popover
interface CurveHistoryButtonProps {
  onApplyDates: (frequency: 'D' | 'W' | 'M', periods: number) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
}

function CurveHistoryButton ({ onApplyDates, isMenuOpen, setIsMenuOpen }: CurveHistoryButtonProps) {
  const handleApplyDates = (frequency: 'D' | 'W' | 'M', periods: number) => {
    onApplyDates(frequency, periods);
  };

  return (
    <Popover open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs px-2 flex items-center justify-center gap-1"
        >
          <Clock className="w-3.5" />
          <span>Curve History</span>
          <ChevronDown
            className={`w-3.5 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" side="bottom" sideOffset={4}>
        <AnalysisMenu
          onApplyDates={handleApplyDates}
          onClose={() => setIsMenuOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

// AddTradeDateButton component - wraps the button and popover with calendar
interface AddTradeDateButtonProps {
  onAddTradeDate: (date: string) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
}

function AddTradeDateButton ({ onAddTradeDate, isMenuOpen, setIsMenuOpen }: AddTradeDateButtonProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      const dateString = format(date, 'yyyy-MM-dd');
      onAddTradeDate(dateString);
      setSelectedDate(undefined);
      setIsMenuOpen(false);
    }
  };

  return (
    <Popover open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs px-2 flex items-center justify-center gap-1"
        >
          <CalendarIcon className="w-3.5" />
          <span>Add trade date</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end" side="bottom" sideOffset={4}>
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleDateSelect}
          initialFocus
          captionLayout="dropdown"
        />
      </PopoverContent>
    </Popover>
  );
}

export default function ForwardCurve ({
  tradeData,
  loading = false,
  onApplyDates,
  onAddTradeDate,
  onResetDates,
  liveData,
}: ForwardCurveProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);

  // State for managing visible curves
  const [visibleCurves, setVisibleCurves] = useState<number[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAddDateMenuOpen, setIsAddDateMenuOpen] = useState(false);

  // Process forward curve data
  const processedData = React.useMemo(() => {
    if (!tradeData || !isArray(tradeData.data) || tradeData.data.length === 0) {
      return { curves: [], contractLabels: [], dates: [], hasLive: false };
    }

    // Filter data to only include items with required fields for forward curve
    const forwardCurveData = filter(tradeData.data, item =>
      item.contract &&
      isNumber(item.value) &&
      !isNaN(item.value) &&
      (item.date || item.forwarded_date || item.original_date)
    );

    if (forwardCurveData.length === 0) {
      return { curves: [], contractLabels: [], dates: [], hasLive: false };
    }

    // Group data by date
    const dataByDate: Record<string, any[]> = {};
    forEach(forwardCurveData, item => {
      const date = item.date || item.forwarded_date || item.original_date;
      if (!dataByDate[date]) {
        dataByDate[date] = [];
      }
      dataByDate[date].push(item);
    });

    const dates = keys(dataByDate).sort();
    if (dates.length === 0) {
      return { curves: [], contractLabels: [], dates: [], hasLive: false };
    }

    // Use the latest date's data to create contract labels
    const latestDateData = dataByDate[dates[dates.length - 1]] || [];
    const sortedLatestData = sortBy(latestDateData, 'sort_order');
    const contractLabels = map(sortedLatestData, item => item.contract);

    // Get all available dates and create curves for each
    const curves = map(dates, (date, dateIndex) => {
      const dateData = filter(dataByDate[date] || [], item => includes(contractLabels, item.contract));
      const sortedDateData = sortBy(dateData, 'sort_order');

      return {
        date,
        data: map(sortedDateData, (item, index) => ({
          time: index as Time,
          value: item.value,
        })),
        color: dateIndex === dates.length - 1 ? '#000000' : // Latest date in black
          // Progress from 0 (oldest) to 1 (newest, excluding latest) drives a
          // solid blue ramp — no opacity — via historicCurveColor above.
          historicCurveColor(dateIndex / Math.max(1, dates.length - 2)),
        lineStyle: 0, // All lines solid
      };
    });

    // Append live curve if live data is available
    let hasLive = false;
    if (liveData && isArray(liveData.data) && liveData.data.length > 0) {
      // Map live data points to the same contract label x-axis indices
      const livePoints: { time: Time; value: number }[] = [];
      const sortedLive = sortBy(liveData.data, 'sort_order');

      forEach(sortedLive, item => {
        const idx = contractLabels.indexOf(item.contract);
        if (idx !== -1 && isNumber(item.value) && !isNaN(item.value)) {
          livePoints.push({ time: idx as Time, value: item.value });
        }
      });

      if (livePoints.length > 0) {
        curves.push({
          date: 'Live',
          data: livePoints,
          color: '#f97316', // Orange
          lineStyle: 0,
        });
        hasLive = true;
      }
    }

    return { curves, contractLabels, dates, hasLive };
  }, [tradeData, liveData]);

  // Initialize visible curves with all curves when data changes
  useEffect(() => {
    if (processedData.curves.length > 0) {
      const allCurves = map(processedData.curves, (_, index) => index);
      setVisibleCurves(allCurves);
    }
  }, [processedData.curves]);

  const toggleCurveVisibility = (curveIndex: number) => {
    setVisibleCurves(prev =>
      includes(prev, curveIndex)
        ? filter(prev, index => index !== curveIndex)
        : [...prev, curveIndex].sort((a, b) => b - a) // Sort descending (latest first)
    );
  };
  const maxCharacterLength = useMemo(() => {
    return Math.max(...map(processedData.contractLabels, label => label.length));
  }, [processedData.contractLabels]);
  useEffect(() => {
    if (!containerRef.current || loading) return;

    // Create chart
    const chart = createChart(containerRef.current, {
      autoSize: true,
      rightPriceScale: {
        visible: true,
        borderVisible: true,
      },
      leftPriceScale: { visible: false },
      layout: {
        background: { type: ColorType.Solid, color: 'white' },
        textColor: '#222',
      },
      grid: {
        vertLines: { visible: true, color: '#e6e6e6' },
        horzLines: { visible: true, color: '#e6e6e6' },
      },
      crosshair: { mode: 0, vertLine: { labelVisible: false } },
      timeScale: {
        fixLeftEdge: true,
        fixRightEdge: true,
        tickMarkMaxCharacterLength: maxCharacterLength,
        // Use categorical axis for contract labels
        timeVisible: false,
        tickMarkFormatter: (time: number) => {
          // Convert numeric time back to contract label
          if (isNumber(time) && processedData.contractLabels[time]) {
            return processedData.contractLabels[time];
          }
          return '';
        },
      },
    });

    chart.timeScale().setVisibleLogicalRange({
      from: 0,
      to: 24,
    });

    chartRef.current = chart;

    // Add watermark
    const img = new window.Image();
    img.src = '/assets/hilo-watermark.png';
    img.onload = () => {
      // Guard against chart being destroyed before image loads
      const pane = chart.panes()?.[0];
      if (!pane) return;
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        createImageWatermark(pane, dataUrl, {
          alpha: 0.05,
          padding: 20,
        });
      }
    };

    const commonSeriesOpts: LineSeriesPartialOptions = {
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: true,
      lineWidth: 1,
    };

    // Crosshair move handler for tooltip
    const handleCrosshairMove = (param: any) => {
      if (param.point === undefined || param.point.x < 0 || param.point.y < 0) {
        return;
      }
      const time = param.time as number;
      const contractLabel = processedData.contractLabels[time] || '';

      // Create tooltip content
      let tooltipContent = '<div style="background: white; border: 1px solid #ccc; border-radius: 4px; padding: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); font-family: Arial, sans-serif; font-size: 12px;">';
      tooltipContent += `<div style="font-weight: bold; margin-bottom: 4px;">${contractLabel}</div>`;

      // Add values for each visible series
      forEach(visibleCurves, curveIndex => {
        const series = seriesMap[curveIndex];
        if (series && param.seriesData.get(series)) {
          const data = param.seriesData.get(series);
          if (data && isNumber(data.value)) {
            const curve = processedData.curves[curveIndex];

            tooltipContent += '<div style="display: flex; align-items: center; margin: 2px 0;">';
            tooltipContent += `<span style="display: inline-block; width: 8px; height: 8px; background-color: ${curve.color}; border-radius: 50%; margin-right: 6px;"></span>`;
            tooltipContent += `<span>${curve.date}: <strong>${data.value.toFixed(2)}</strong></span>`;
            tooltipContent += '</div>';
          }
        }
      });

      tooltipContent += '</div>';

      // Update tooltip position and content
      const tooltip = document.getElementById('forward-curve-tooltip');
      if (tooltip && containerRef.current) {
        tooltip.innerHTML = tooltipContent;
        tooltip.style.display = 'block';

        // Get container width to determine if cursor is in right portion
        const containerWidth = containerRef.current.clientWidth;
        const cursorX = param.point.x;
        const isInRightHalf = cursorX > containerWidth * 0.5;

        // Position tooltip to the left if cursor is in right half of chart
        if (isInRightHalf) {
          // Force a reflow to get accurate tooltip width
          void tooltip.offsetWidth;
          const tooltipWidth = tooltip.getBoundingClientRect().width || 200;
          const leftPosition = cursorX - tooltipWidth - 10;
          // Ensure tooltip doesn't go off the left edge
          tooltip.style.left = `${Math.max(10, leftPosition)}px`;
        } else {
          tooltip.style.left = `${cursorX + 10}px`;
        }

        tooltip.style.top = `${param.point.y - 10}px`;
      }
    };

    // Unsubscribe crosshair when cursor leaves chart, resubscribe on enter
    const containerEl = containerRef.current;
    const handleMouseLeave = () => {
      try {
        chart.unsubscribeCrosshairMove(handleCrosshairMove);
      } catch (error) {
        console.warn('Forward curve chart: unable to unsubscribe crosshair move on mouse leave', error);
      }
      const tooltip = document.getElementById('forward-curve-tooltip');
      if (tooltip) {
        tooltip.style.display = 'none';
      }
    };

    const handleMouseEnter = () => {
      chart.subscribeCrosshairMove(handleCrosshairMove);
    };

    // Add series for each visible curve if we have data
    const seriesMap: Record<number, any> = {};
    if (processedData.contractLabels.length > 0) {
      forEach(processedData.curves, (curve, index) => {
        const isLiveCurve = curve.date === 'Live';
        // Latest settle curve is the last non-live curve
        const lastSettleIndex = processedData.hasLive ? processedData.curves.length - 2 : processedData.curves.length - 1;
        const isLatestSettleCurve = index === lastSettleIndex;
        const series = chart.addSeries(LineSeries, {
          ...commonSeriesOpts,
          color: curve.color,
          lineStyle: curve.lineStyle,
          lineWidth: (isLatestSettleCurve || isLiveCurve) ? 2 : 1,
        });
        seriesMap[index] = series;
      });

      // Set data for visible curves only
      forEach(visibleCurves, curveIndex => {
        if (seriesMap[curveIndex] && processedData.curves[curveIndex]) {
          seriesMap[curveIndex].setData(processedData.curves[curveIndex].data);
        }
      });

      // Clear data for hidden curves
      forEach(processedData.curves, (_, index) => {
        if (!includes(visibleCurves, index) && seriesMap[index]) {
          seriesMap[index].setData([]);
        }
      });

      // Subscribe to crosshair move to show custom tooltip
      chart.subscribeCrosshairMove(handleCrosshairMove);

      containerEl?.addEventListener('mouseleave', handleMouseLeave);
      containerEl?.addEventListener('mouseenter', handleMouseEnter);
    }

    // Do not attach our own ResizeObserver when using autoSize: true.
    // lightweight-charts already manages resizing internally, and a second
    // observer that mutates options can trigger ResizeObserver loops.

    return () => {
      // No custom ResizeObserver to clean up
      // Remove mouse listeners and unsubscribe crosshair
      containerEl?.removeEventListener('mouseleave', handleMouseLeave);
      containerEl?.removeEventListener('mouseenter', handleMouseEnter);
      try {
        chart.unsubscribeCrosshairMove(handleCrosshairMove);
      } catch (error) {
        console.warn('Forward curve chart: unable to unsubscribe crosshair move during cleanup', error);
      }
      chart.remove();
    };
  }, [processedData, visibleCurves, loading, maxCharacterLength]);

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full w-full text-gray-400">
        Loading...
      </div>
    );
  }

  const handleApplyDates = (frequency: 'D' | 'W' | 'M', periods: number) => {
    if (onApplyDates) {
      onApplyDates(frequency, periods);
    }
  };

  // Show empty state if no data
  if (!tradeData || !isArray(tradeData.data) || tradeData.data.length === 0 || processedData.contractLabels.length === 0 || processedData.curves.length === 0) {
    return (
      <div className="h-full w-full flex flex-col">
        {/* Legend with Curve History button even when no data */}
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-4 py-2 border-b">
          {onApplyDates ? (
            <CurveHistoryButton
              onApplyDates={handleApplyDates}
              isMenuOpen={isMenuOpen}
              setIsMenuOpen={setIsMenuOpen}
            />
          ) : (
            <div />
          )}
          <div />
          <div className="flex flex-col gap-1">
            {onAddTradeDate ? (
              <AddTradeDateButton
                onAddTradeDate={onAddTradeDate}
                isMenuOpen={isAddDateMenuOpen}
                setIsMenuOpen={setIsAddDateMenuOpen}
              />
            ) : (
              <div />
            )}
            {onResetDates ? (
              <Button
                variant="outline"
                size="sm"
                onClick={onResetDates}
                className="h-7 text-xs px-2 flex items-center justify-center gap-1"
              >
                <span>Reset</span>
              </Button>
            ) : (
              <div />
            )}
          </div>
        </div>
        <div className="flex items-center justify-center flex-grow text-gray-400">
          No forward curve data available
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* Legend */}
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-4 py-2 border-b">
        {/* Curve History button on the left */}
        {onApplyDates ? (
          <CurveHistoryButton
            onApplyDates={handleApplyDates}
            isMenuOpen={isMenuOpen}
            setIsMenuOpen={setIsMenuOpen}
          />
        ) : (
          <div />
        )}
        {/* Centered legend items */}
        <div className="flex justify-center">
          <ChipLegend
            items={map(processedData.curves, (curve, index): ChipLegendItem => {
              const isLiveCurve = curve.date === 'Live';
              const lastSettleIndex = processedData.hasLive ? processedData.curves.length - 2 : processedData.curves.length - 1;
              const isLatestSettleCurve = index === lastSettleIndex;
              return {
                key: String(index),
                label: curve.date,
                color: curve.color,
                hidden: !includes(visibleCurves, index),
                emphasized: isLatestSettleCurve || isLiveCurve,
              };
            })}
            onToggle={(key) => toggleCurveVisibility(Number(key))}
          />
        </div>
        {/* Add trade date button and Reset button on the right */}
        <div className="flex flex-col gap-1">
          {onAddTradeDate ? (
            <AddTradeDateButton
              onAddTradeDate={onAddTradeDate}
              isMenuOpen={isAddDateMenuOpen}
              setIsMenuOpen={setIsAddDateMenuOpen}
            />
          ) : (
            <div />
          )}
          {onResetDates ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onResetDates}
              className="h-7 text-xs px-2 flex items-center justify-center gap-1"
            >
              <span>Reset</span>
            </Button>
          ) : (
            <div />
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="flex-grow min-h-0 min-w-0 relative overflow-hidden" ref={containerRef}>
        {/* Custom tooltip */}
        <div
          id="forward-curve-tooltip"
          style={{
            position: 'absolute',
            display: 'none',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        />
      </div>
    </div>
  );
}
